import type { CustomChartGrammar } from '@erp/viz/custom';
import type {
  ReportDatasetColumn,
  ReportDatasetRow,
} from '../ReportDataContext';

/** The most categories a starter bar chart draws before it keeps only the top ones. */
const STARTER_MAX_CATEGORIES = 20;

/**
 * The columns a starter chart plots: the first Number, and the text column
 * with the fewest distinct values (at least two) — a Grade or Region, not a
 * Product Key whose labels would pile on top of each other. Without rows, the
 * first text column. `categories` is how many distinct values it has.
 */
function starterColumns(
  columns: readonly ReportDatasetColumn[],
  rows: readonly ReportDatasetRow[],
) {
  const value = columns.find((c) => c.dataType === 'number');
  const texts = columns.filter((c) => c.dataType !== 'number');
  const distinct = (column: ReportDatasetColumn) =>
    new Set(rows.map((row) => row[column.key] ?? null)).size;
  const ranked = texts
    .map((column) => ({ column, categories: distinct(column) }))
    .sort(
      (a, b) =>
        Number(b.categories >= 2) - Number(a.categories >= 2) ||
        a.categories - b.categories,
    );
  const best = rows.length ? ranked[0] : undefined;
  const category = best?.column ?? texts[0];
  if (!category || !value) return null;
  return { category, value, categories: best?.categories ?? 0 };
}

/**
 * A minimal Vega-Lite bar spec seeded from a dataset's columns: first
 * categorical column on x, first numeric column summed on y. Used by the
 * config form's "Insert starter spec" action so authors (human or agent)
 * start from something that renders instead of an empty editor. Returns null
 * when the dataset can't support it (no categorical/numeric pair).
 *
 * The spec deliberately has no `data` — the canvas always injects the bound
 * dataset's rows at render time.
 */
export function starterVegaSpec(
  columns: ReportDatasetColumn[],
  rows: readonly ReportDatasetRow[] = [],
): Record<string, unknown> | null {
  const picked = starterColumns(columns, rows);
  if (!picked) return null;
  const { category, value } = picked;
  // Too many categories to read: the top ones by value.
  const top =
    picked.categories > STARTER_MAX_CATEGORIES
      ? {
          transform: [
            {
              aggregate: [{ op: 'sum', field: value.key, as: value.key }],
              groupby: [category.key],
            },
            {
              window: [{ op: 'rank', as: '__rank' }],
              sort: [{ field: value.key, order: 'descending' }],
            },
            { filter: `datum.__rank <= ${STARTER_MAX_CATEGORIES}` },
          ],
        }
      : {};
  return {
    ...top,
    mark: { type: 'bar', cornerRadius: 4 },
    encoding: {
      x: { field: category.key, type: 'nominal', title: category.label },
      y: {
        field: value.key,
        type: 'quantitative',
        aggregate: 'sum',
        title: value.label,
      },
    },
  };
}

/**
 * The same starter bar chart in full Vega: the dataset read from `table`,
 * summed per category, with a tooltip — a working base to extend with what
 * only Vega can do.
 */
function starterVegaGrammarSpec(
  columns: ReportDatasetColumn[],
  rows: readonly ReportDatasetRow[],
): Record<string, unknown> | null {
  const picked = starterColumns(columns, rows);
  if (!picked) return null;
  const { category, value } = picked;
  return {
    data: [
      {
        name: 'table',
        transform: [
          {
            type: 'aggregate',
            groupby: [category.key],
            fields: [value.key],
            ops: ['sum'],
            as: ['total'],
          },
        ],
      },
    ],
    scales: [
      {
        name: 'x',
        type: 'band',
        domain: { data: 'table', field: category.key },
        range: 'width',
        padding: 0.2,
      },
      {
        name: 'y',
        domain: { data: 'table', field: 'total' },
        nice: true,
        range: 'height',
      },
    ],
    axes: [
      { orient: 'bottom', scale: 'x', title: category.label },
      { orient: 'left', scale: 'y', title: value.label, tickCount: 5 },
    ],
    marks: [
      {
        type: 'rect',
        from: { data: 'table' },
        encode: {
          enter: {
            x: { scale: 'x', field: category.key },
            width: { scale: 'x', band: 1 },
            y: { scale: 'y', field: 'total' },
            y2: { scale: 'y', value: 0 },
            cornerRadiusTopLeft: { value: 4 },
            cornerRadiusTopRight: { value: 4 },
            tooltip: {
              signal: `{${JSON.stringify(category.label)}: datum[${JSON.stringify(category.key)}], ${JSON.stringify(value.label)}: datum.total}`,
            },
          },
        },
      },
    ],
  };
}

/** The starter spec for a grammar (see {@link starterVegaSpec}). */
export function starterSpecFor(
  grammar: CustomChartGrammar,
  columns: ReportDatasetColumn[],
  rows: readonly ReportDatasetRow[] = [],
): Record<string, unknown> | null {
  return grammar === 'vega'
    ? starterVegaGrammarSpec(columns, rows)
    : starterVegaSpec(columns, rows);
}
